import { memo, useEffect, useCallback, useRef } from "react";
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { currentProjectAtom } from "~/stores/ProjectStore";
import { isMapLoadedDoneAtom } from "~/components/Map/stores/mapStore";
import { projectMembersLocationAtom, memberDisplayedBoundsAtom, showMemberLabelsAtom } from "./MemberStore";
import { userProfileModalAtom } from "~/components/Map/components/UserProfile/UserProfileStore";
import { UserLocation_Base } from "~/common/typescript/models/UserLocation";
import { UserLocationAPIs } from "~/common/typescript/apis/UserLocationAPIs";
import { LngLatBounds } from "maplibre-gl";
import * as Turf from '@turf/turf';
import { MemberMapUtilOptimized } from "./MemberMapUtilOptimized";
import maplibregl from 'maplibre-gl';

const MemberMapController = () => {
  const [currentProject, setCurrentProject] = useAtom(currentProjectAtom);
  const [isMapLoadedDone, setIsMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const [projectMembersLocation, setProjectMembersLocation] = useAtom(projectMembersLocationAtom);
  const setMemberDisplayedBounds = useSetAtom(memberDisplayedBoundsAtom);
  const memberDisplayedBounds = useAtomValue(memberDisplayedBoundsAtom);
  const showMemberLabels = useAtomValue(showMemberLabelsAtom);
  const setUserProfileModal = useSetAtom(userProfileModalAtom);

  // Auto-refresh refs
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const isAutoRefreshEnabled = useRef(true); // Default enabled

  const fetchData = async (isAutoRefresh = false) => {
    if (!currentProject || !currentProject.id) return;
    try {
      if (isAutoRefresh) {
        console.log('Auto-refreshing member locations...');
      }

      // Fetch only member locations (do not load users with roles on map load)
      const membersLocation = await UserLocationAPIs.remote_getUserLocationOfProject(currentProject.id);

      setProjectMembersLocation(membersLocation || []);
    } catch (error) {
      console.error("Error fetching project members location:", error);
      setProjectMembersLocation([]);
    }
  };

  // Start auto-refresh
  const startAutoRefresh = useCallback(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
    }

    if (isAutoRefreshEnabled.current && currentProject?.id) {
      autoRefreshInterval.current = setInterval(() => {
        fetchData(true);
      }, 5000); // 5 seconds

      console.log('Member location auto-refresh started (5s interval)');
    }
  }, [currentProject?.id]);

  // Stop auto-refresh
  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
      console.log('Member location auto-refresh stopped');
    }
  }, []);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    isAutoRefreshEnabled.current = enabled;
    if (enabled) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
  }, [startAutoRefresh, stopAutoRefresh]);

  useEffect(() => {
    if (!currentProject || !isMapLoadedDone) return;

    fetchData();

    // Start auto-refresh when project loads
    if (isAutoRefreshEnabled.current) {
      startAutoRefresh();
    }

    // Cleanup on project change
    return () => {
      stopAutoRefresh();
    };
  }, [currentProject, isMapLoadedDone, startAutoRefresh, stopAutoRefresh]);

  // Update map when projectMembersLocation or showMemberLabels changes
  useEffect(() => {
    if (!isMapLoadedDone) return;
    MemberMapUtilOptimized.updateMembers(projectMembersLocation, showMemberLabels);
  }, [isMapLoadedDone, projectMembersLocation, showMemberLabels]);

  useEffect(() => {
    if (!isMapLoadedDone) return;

    let combinedMemberBounds: LngLatBounds | null = null;
    projectMembersLocation.forEach(memberLocation => {
      if (memberLocation.location) {
        try {
          const locationObj = JSON.parse(memberLocation.location);
          let memberBounds: LngLatBounds | null = null;

          // Handle GeoJSON format
          if (locationObj.type === "Point" && locationObj.coordinates) {
            const bbox = Turf.bbox(locationObj);
            memberBounds = new LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]);
          }
          // Handle lat/lng format
          else if (locationObj.latitude && locationObj.longitude) {
            const lng = locationObj.longitude;
            const lat = locationObj.latitude;
            memberBounds = new LngLatBounds([lng, lat], [lng, lat]);
          }

          if (memberBounds) {
            if (combinedMemberBounds) {
              combinedMemberBounds.extend(memberBounds);
            } else {
              combinedMemberBounds = memberBounds;
            }
          }
        } catch (error) {
          console.error("Error parsing location for member:", memberLocation.user.id, error);
        }
      }
    });

    if (combinedMemberBounds) {
      setMemberDisplayedBounds(JSON.stringify([
        [combinedMemberBounds.getWest(), combinedMemberBounds.getSouth()],
        [combinedMemberBounds.getEast(), combinedMemberBounds.getNorth()],
      ]));
    } else {
      setMemberDisplayedBounds(null);
    }
  }, [isMapLoadedDone, projectMembersLocation, setMemberDisplayedBounds]);

  // Function to zoom to member bounds - can be called externally
  const zoomToMemberBounds = useCallback(() => {
    if (!window.map || !memberDisplayedBoundsAtom) return;

    try {
      const boundsString = memberDisplayedBounds;
      if (boundsString) {
        const bounds = JSON.parse(boundsString);
        const lngLatBounds = new LngLatBounds(bounds[0], bounds[1]);
        window.map.fitBounds(lngLatBounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    } catch (error) {
      console.error("Error zooming to member bounds:", error);
    }
  }, [memberDisplayedBounds]);

  // Make functions available globally for other components to use
  useEffect(() => {
    (window as any).zoomToMemberBounds = zoomToMemberBounds;
    (window as any).toggleMemberAutoRefresh = toggleAutoRefresh;
    (window as any).refreshMemberLocations = () => fetchData(false);

    return () => {
      delete (window as any).zoomToMemberBounds;
      delete (window as any).toggleMemberAutoRefresh;
      delete (window as any).refreshMemberLocations;
    };
  }, [zoomToMemberBounds, toggleAutoRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoRefresh();
    };
  }, [stopAutoRefresh]);

  // Setup click handler for member markers
  useEffect(() => {
    if (!window.map || !isMapLoadedDone) return;

    const handleMemberClick = (e: maplibregl.MapMouseEvent) => {
      if (!e.features || e.features.length === 0) return;

      const feature = e.features[0];
      const userId = feature.properties?.userId;

      if (userId) {
        console.log('Member marker clicked, opening profile for userId:', userId);
        setUserProfileModal({
          visible: true,
          userId: userId
        });
      }
    };

    const handleCursorChange = () => {
      if (window.map) {
        window.map.getCanvas().style.cursor = 'pointer';
      }
    };

    const handleCursorReset = () => {
      if (window.map) {
        window.map.getCanvas().style.cursor = '';
      }
    };

    // Add click handler to member layer
    window.map.on('click', MemberMapUtilOptimized._layerId, handleMemberClick);
    window.map.on('mouseenter', MemberMapUtilOptimized._layerId, handleCursorChange);
    window.map.on('mouseleave', MemberMapUtilOptimized._layerId, handleCursorReset);

    return () => {
      if (window.map) {
        window.map.off('click', MemberMapUtilOptimized._layerId, handleMemberClick);
        window.map.off('mouseenter', MemberMapUtilOptimized._layerId, handleCursorChange);
        window.map.off('mouseleave', MemberMapUtilOptimized._layerId, handleCursorReset);
      }
    };
  }, [isMapLoadedDone, setUserProfileModal]);

  return (<div style={{ display: 'none' }}></div>);
};

export default memo(MemberMapController);
